/**
 * Histórico de documentos emitidos (#344).
 *
 * A tarefa desta tela é reencontrar e baixar um documento já entregue — não emitir um novo,
 * que tem tela própria e fica no canto superior, subordinado.
 *
 * Uma linha por DOCUMENTO, na revisão vigente, e não uma linha por revisão: a lista responde
 * "que documentos esta empresa tem". Como as revisões são contíguas a partir de 1, o número
 * da revisão vigente também é o total — é daí que sai o "3 de 3", sem pedir mais nada ao
 * servidor. O histórico atrás dela abre em diálogo, ao clicar na linha.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert } from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';

import { pode } from '../../authorization/pode';
import {
  DataTable,
  PageShell,
  RouteActionButton,
  type DataTableColumn,
} from '../../components/common';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { documentService } from '../../services/documentService';
import { openSignedFile } from '../../services/fileAccess';
import type {
  DocumentEmissionRecord,
  EmissionSummary,
} from '../../types/document';
import {
  DocumentHistoryFilters,
  FILTROS_VAZIOS,
  SituacaoChip,
  temFiltroAplicado,
  type FiltrosDoHistorico,
} from './components/DocumentHistoryFilters';
import { ArquivosDaEmissao, RevisionsDialog } from './components/RevisionsDialog';
import { formatarDia, formatarPeriodo, situacaoDaEmissao } from './situacao';

const SEM_CONTAGEM: EmissionSummary = { vigente: 0, aVencer: 0, vencido: 0, semValidade: 0 };

const MIGALHAS = [
  { label: 'Início', path: '/home' },
  { label: 'Segurança do Trabalho' },
  { label: 'Documentos Emitidos' },
];

export const DocumentHistoryPage: React.FC = () => {
  const { effectiveCompanyIdString } = useCompanyContext();

  const [filtros, setFiltros] = useState<FiltrosDoHistorico>({
    ...FILTROS_VAZIOS,
    companyId: effectiveCompanyIdString,
  });
  const [registros, setRegistros] = useState<DocumentEmissionRecord[]>([]);
  const [summary, setSummary] = useState<EmissionSummary>(SEM_CONTAGEM);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [revisoesDe, setRevisoesDe] = useState<DocumentEmissionRecord | null>(null);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(null);

    documentService
      .listEmissions({
        companyId: filtros.companyId === '' ? undefined : Number(filtros.companyId),
        type: filtros.type || undefined,
        periodStart: filtros.periodStart || undefined,
        periodEnd: filtros.periodEnd || undefined,
        status: filtros.status || undefined,
        page: pagina + 1,
        limit: porPagina,
      })
      .then((resultado) => {
        if (!ativo) return;
        setRegistros(resultado.data);
        setSummary(resultado.summary);
        setTotal(resultado.pagination.total);
      })
      .catch((err: unknown) => {
        if (!ativo) return;
        setRegistros([]);
        setErro(
          err instanceof Error ? err.message : 'Não foi possível ler os documentos emitidos'
        );
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, [filtros, pagina, porPagina]);

  /** Filtro novo recomeça na primeira página: continuar na quinta mostraria vazio à toa. */
  const trocarFiltros = useCallback((novos: FiltrosDoHistorico) => {
    setFiltros(novos);
    setPagina(0);
  }, []);

  const limparFiltros = useCallback(() => {
    setFiltros((atuais) => ({ ...FILTROS_VAZIOS, companyId: atuais.companyId }));
    setPagina(0);
  }, []);

  /**
   * A janela abre no INÍCIO do clique, antes de qualquer espera: o token do arquivo é
   * assinado numa chamada à parte, e o bloqueador de pop-up engole a janela que nasce depois
   * dela. E o clique para aqui — sem isso, baixar um arquivo também abriria o diálogo das
   * revisões por cima do download.
   */
  const baixar = useCallback((caminho: string, evento: React.MouseEvent) => {
    evento.stopPropagation();
    const janela = window.open('about:blank', '_blank');
    if (!janela) return;

    openSignedFile(caminho, janela).catch((err: unknown) => {
      setErro(err instanceof Error ? err.message : 'Não foi possível abrir o arquivo');
    });
  }, []);

  const colunas: DataTableColumn<DocumentEmissionRecord>[] = [
    {
      id: 'documento',
      label: 'Documento',
      primary: true,
      render: (registro) => (
        <>
          <div style={{ fontWeight: 500 }}>{registro.typeLabel}</div>
          <div style={{ fontSize: '0.78rem', opacity: 0.7 }}>{registro.scopeName}</div>
        </>
      ),
    },
    {
      id: 'periodo',
      label: 'Período',
      render: (registro) => formatarPeriodo(registro.periodStart, registro.periodEnd),
    },
    {
      id: 'versao',
      label: 'Versão',
      render: (registro) => <strong>{registro.version}</strong>,
    },
    {
      id: 'emitidoEm',
      label: 'Emitido em',
      render: (registro) => formatarDia(registro.emittedAt),
    },
    {
      id: 'validoAte',
      label: 'Válido até',
      render: (registro) => formatarDia(registro.validUntil),
    },
    {
      id: 'responsavel',
      label: 'Responsável técnico',
      render: (registro) => registro.technicalResponsibleName ?? '—',
    },
    {
      id: 'anexos',
      label: 'Anexos',
      render: (registro) =>
        registro.attachments.length === 0 ? '—' : registro.attachments.map((anexo) => anexo.title).join(', '),
    },
    {
      id: 'situacao',
      label: 'Situação',
      render: (registro) => <SituacaoChip situacao={situacaoDaEmissao(registro)} />,
    },
    {
      id: 'arquivo',
      label: 'Arquivo',
      align: 'right',
      render: (registro) => (
        <ArquivosDaEmissao
          pdfPath={registro.pdfPath}
          docxPath={registro.docxPath}
          onBaixar={baixar}
        />
      ),
    },
  ];

  return (
    <PageShell
      breadcrumbs={MIGALHAS}
      title="Documentos Emitidos"
      subtitle="Histórico do que já foi entregue, com todas as revisões e o arquivo de cada uma"
      actions={
        <RouteActionButton
          para="/documents/history/emitir"
          exige={pode('document_emission.read')}
          variant="contained"
          startIcon={<DescriptionIcon />}
        >
          Emitir documento
        </RouteActionButton>
      }
    >
      <DocumentHistoryFilters
        filtros={filtros}
        onChange={trocarFiltros}
        summary={summary}
        carregando={carregando}
      />

      {erro && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {erro}
        </Alert>
      )}

      <DataTable
        columns={colunas}
        rows={registros}
        getRowId={(registro) => registro.id}
        loading={carregando}
        onRowClick={setRevisoesDe}
        filtersActive={temFiltroAplicado(filtros)}
        onClearFilters={limparFiltros}
        emptyState={{
          title: 'Nenhum documento emitido para esta empresa',
          description:
            'O histórico começa na primeira emissão. Documentos gerados antes desta tela existir não aparecem aqui, e não há como recriá-los.',
          action: {
            label: 'Emitir documento',
            node: 'document-emission',
            deniedHint: 'Peça a quem emite documentos nesta empresa.',
          },
        }}
        pagination={{
          page: pagina,
          rowsPerPage: porPagina,
          totalCount: total,
          onPageChange: setPagina,
          onRowsPerPageChange: (quantidade) => {
            setPorPagina(quantidade);
            setPagina(0);
          },
        }}
      />

      {revisoesDe && (
        <RevisionsDialog
          registro={revisoesDe}
          onClose={() => setRevisoesDe(null)}
          onBaixar={baixar}
        />
      )}
    </PageShell>
  );
};

export default DocumentHistoryPage;
